// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * UniverseManager - Universe Configuration Validator
 * 
 * Validates universe configurations at build time and runtime:
 * - Warp gate pairing (bidirectional connectivity)
 * - Alien species assignments
 * - Required properties
 * - Distance/reachability checks
 * 
 * Usage:
 *   import { validateUniverse, validateAllUniverses } from './utils/UniverseManager';
 *   
 *   // Validate on universe load
 *   const issues = validateUniverse(universeConfig);
 *   if (issues.critical.length > 0) {
 *     console.error('Critical universe issues:', issues);
 *   }
 */

import AlienSpecies from '../config/AlienSpeciesConfig';
import STORAGE_KEYS from './system/STORAGE_KEYS';

// Calculate 3D distance
function distance3D(a, b) {
  if (!a || !b) return Infinity;
  return Math.sqrt(
    Math.pow(b.x - a.x, 2) +
    Math.pow(b.y - a.y, 2) +
    Math.pow(b.z - a.z, 2)
  );
}

/**
 * Validate a single universe configuration
 * @param {Object} universe - Universe config object
 * @param {Object} allUniverses - All universe configs (for gate validation)
 * @returns {Object} - { critical: [], warnings: [], info: [] }
 */
export function validateUniverse(universe, allUniverses = {}) {
  const issues = {
    critical: [],
    warnings: [],
    info: []
  };

  if (!universe) {
    issues.critical.push({ type: 'MISSING_UNIVERSE', message: 'Universe config is null or undefined' });
    return issues;
  }

  const { id, starSystems, warpGates, aliens } = universe;

  // Validate basic structure
  if (!id) issues.critical.push({ type: 'MISSING_ID', message: 'Universe missing id property' });
  if (!starSystems || !Array.isArray(starSystems)) {
    issues.critical.push({ type: 'MISSING_SYSTEMS', message: 'Universe missing starSystems array' });
    return issues; // Can't continue without systems
  }

  // Validate star systems
  validateStarSystems(starSystems, issues);

  // Validate warp gates
  if (warpGates && Array.isArray(warpGates)) {
    validateWarpGates(warpGates, id, allUniverses, starSystems, issues);
  }

  // Validate aliens
  if (aliens) {
    validateAliens(aliens, starSystems, issues);
  } else {
    issues.warnings.push({ type: 'NO_ALIENS', message: 'Universe has no alien configuration' });
  }

  return issues;
}

/**
 * Validate star systems
 */
function validateStarSystems(systems, issues) {
  const requiredProps = ['id', 'name', 'type', 'coordinates', 'services', 'tradeType'];
  const refuelCount = systems.filter(s => s.services?.includes('refuel')).length;

  systems.forEach((system, index) => {
    // Check required properties
    const missing = requiredProps.filter(prop => !system[prop]);
    if (missing.length > 0) {
      issues.critical.push({
        type: 'MISSING_PROPERTIES',
        system: system.name || system.id || `index ${index}`,
        missing: missing.join(', ')
      });
    }

    // Check refuel availability (except black holes/anomalies)
    if (system.type !== 'blackhole' && system.type !== 'anomaly') {
      if (!system.services?.includes('refuel')) {
        issues.critical.push({
          type: 'MISSING_REFUEL',
          system: system.name,
          message: 'System missing refuel service - players could get stranded'
        });
      }
    }

    // Check coordinates
    if (system.coordinates) {
      const { x, y, z } = system.coordinates;
      if (typeof x !== 'number' || typeof y !== 'number' || typeof z !== 'number') {
        issues.critical.push({
          type: 'INVALID_COORDINATES',
          system: system.name,
          message: 'Coordinates must be numbers'
        });
      }
    }
  });

  // Check minimum distance between systems
  for (let i = 0; i < systems.length; i++) {
    for (let j = i + 1; j < systems.length; j++) {
      const dist = distance3D(systems[i].coordinates, systems[j].coordinates);
      if (dist < 3) {
        issues.warnings.push({
          type: 'SYSTEMS_TOO_CLOSE',
          system1: systems[i].name,
          system2: systems[j].name,
          distance: dist.toFixed(2),
          message: 'Systems closer than 3 LY may overlap visually'
        });
      }
    }
  }

  // Check reachability network
  const refuelSystems = systems.filter(s => s.services?.includes('refuel'));
  systems.forEach(system => {
    const nearest = refuelSystems
      .filter(r => r.id !== system.id)
      .map(r => ({ name: r.name, distance: distance3D(system.coordinates, r.coordinates) }))
      .sort((a, b) => a.distance - b.distance)[0];

    if (nearest && nearest.distance > 210) {
      issues.critical.push({
        type: 'SYSTEM_UNREACHABLE',
        system: system.name,
        nearestRefuel: nearest.name,
        distance: nearest.distance.toFixed(1),
        message: 'System beyond 210 LY from refuel - players could get stranded'
      });
    }
  });

  // Info: refuel availability
  issues.info.push({
    type: 'REFUEL_COUNT',
    count: refuelCount,
    total: systems.length,
    percentage: ((refuelCount / systems.length) * 100).toFixed(1)
  });
}

/**
 * Validate warp gates
 */
function validateWarpGates(gates, universeId, allUniverses, systems, issues) {
  const universe = allUniverses[universeId];
  
  gates.forEach(gate => {
    // Check required properties
    if (!gate.id) {
      issues.critical.push({ type: 'MISSING_GATE_ID', gate: gate.name || 'unknown' });
      return;
    }

    const toUniverse = gate.toUniverse || gate.destinationUniverse;
    const destinationGate = gate.destinationGate;

    if (!toUniverse) {
      issues.critical.push({
        type: 'MISSING_TARGET_UNIVERSE',
        gate: gate.name,
        message: 'Gate missing toUniverse/destinationUniverse property'
      });
      return;
    }

    if (!destinationGate) {
      issues.critical.push({
        type: 'MISSING_DESTINATION_GATE',
        gate: gate.name,
        message: 'Gate missing destinationGate property - players could get trapped'
      });
      return;
    }

    // Check if target universe exists
    const targetUniverse = allUniverses[toUniverse];
    if (!targetUniverse) {
      issues.critical.push({
        type: 'TARGET_UNIVERSE_NOT_FOUND',
        gate: gate.name,
        targetUniverse: toUniverse,
        message: 'Target universe does not exist - players will get trapped'
      });
      return;
    }

    // Check if return gate exists
    const returnGate = targetUniverse.warpGates?.find(g => g.id === destinationGate);
    if (!returnGate) {
      issues.critical.push({
        type: 'RETURN_GATE_NOT_FOUND',
        gate: gate.name,
        targetUniverse: toUniverse,
        expectedGateId: destinationGate,
        message: 'Return gate does not exist - players will get trapped'
      });
      return;
    }

    // Check if return gate points back
    const returnTargetUniverse = returnGate.toUniverse || returnGate.destinationUniverse;
    if (returnTargetUniverse !== universeId) {
      issues.critical.push({
        type: 'RETURN_GATE_MISMATCH',
        gate: gate.name,
        returnGate: returnGate.name,
        expected: universeId,
        actual: returnTargetUniverse,
        message: 'Return gate points to wrong universe'
      });
    }

    if (returnGate.destinationGate !== gate.id) {
      issues.critical.push({
        type: 'GATE_ID_MISMATCH',
        gate: gate.name,
        returnGate: returnGate.name,
        expected: gate.id,
        actual: returnGate.destinationGate,
        message: 'Return gate references wrong gate ID'
      });
    }

    // Check gate reachability (if coordinates available)
    const gateCoords = gate.coordinates || universe.warpGatePositions?.[gate.id];
    if (gateCoords && systems.length > 0) {
      const refuelSystems = systems.filter(s => s.services?.includes('refuel'));
      const nearest = refuelSystems
        .map(s => ({ name: s.name, distance: distance3D(gateCoords, s.coordinates) }))
        .sort((a, b) => a.distance - b.distance)[0];

      if (nearest && nearest.distance > 210) {
        issues.critical.push({
          type: 'GATE_UNREACHABLE',
          gate: gate.name,
          nearestRefuel: nearest.name,
          distance: nearest.distance.toFixed(1),
          message: 'Gate beyond 210 LY from refuel - players could get stranded'
        });
      }
    }
  });

  issues.info.push({ type: 'GATE_COUNT', count: gates.length });
}

/**
 * Validate alien configuration
 */
function validateAliens(aliens, systems, issues) {
  if (!aliens.planets) {
    issues.critical.push({
      type: 'MISSING_PLANETS_MAPPING',
      message: 'Aliens config missing planets property - aliens will not appear in scans'
    });
    return;
  }

  const systemIds = new Set(systems.map(s => s.id));
  const planetsWithAliens = Object.keys(aliens.planets).map(id => parseInt(id));
  
  // Check for alien assignments to non-existent systems
  planetsWithAliens.forEach(planetId => {
    if (!systemIds.has(planetId)) {
      issues.warnings.push({
        type: 'ALIEN_SYSTEM_NOT_FOUND',
        systemId: planetId,
        message: 'Aliens assigned to non-existent system'
      });
    }
  });

  // Check for undefined alien species
  Object.entries(aliens.planets).forEach(([systemId, alienIds]) => {
    if (!Array.isArray(alienIds)) {
      issues.critical.push({
        type: 'INVALID_ALIEN_ARRAY',
        systemId,
        message: 'Alien assignment must be an array'
      });
      return;
    }

    alienIds.forEach(alienId => {
      if (!AlienSpecies[alienId]) {
        issues.critical.push({
          type: 'ALIEN_SPECIES_NOT_FOUND',
          systemId,
          alienId,
          message: `Alien species '${alienId}' not registered in AlienSpeciesConfig.js`
        });
      }
    });
  });

  // Calculate coverage
  const systemsWithAliens = planetsWithAliens.filter(id => 
    systemIds.has(id) && aliens.planets[id].length > 0
  ).length;
  const coverage = ((systemsWithAliens / systems.length) * 100).toFixed(1);

  issues.info.push({
    type: 'ALIEN_COVERAGE',
    systemsWithAliens,
    totalSystems: systems.length,
    coverage: `${coverage}%`
  });

  if (coverage < 80) {
    issues.warnings.push({
      type: 'LOW_ALIEN_COVERAGE',
      coverage: `${coverage}%`,
      message: 'Less than 80% of systems have aliens - consider adding more'
    });
  }
}

/**
 * Validate all universes
 * @param {Object} universes - Object with universe configs { id: config }
 * @returns {Object} - { universeId: issues }
 */
export function validateAllUniverses(universes) {
  const results = {};
  
  Object.entries(universes).forEach(([id, universe]) => {
    results[id] = validateUniverse(universe, universes);
  });

  return results;
}

/**
 * Get summary of validation results
 */
export function getValidationSummary(results) {
  let totalCritical = 0;
  let totalWarnings = 0;
  const failedUniverses = [];

  Object.entries(results).forEach(([id, issues]) => {
    totalCritical += issues.critical.length;
    totalWarnings += issues.warnings.length;
    
    if (issues.critical.length > 0) {
      failedUniverses.push({
        id,
        criticalCount: issues.critical.length,
        warningCount: issues.warnings.length
      });
    }
  });

  return {
    totalUniverses: Object.keys(results).length,
    totalCritical,
    totalWarnings,
    failedUniverses,
    passed: totalCritical === 0
  };
}

/**
 * Runtime validation hook - validates universe when entering
 * Use in NavigationPanel or universe switch logic
 */
export function validateUniverseOnEnter(universe, allUniverses) {
  // Only validate in development mode
  if (process.env.NODE_ENV !== 'development') {
    return { valid: true };
  }

  const issues = validateUniverse(universe, allUniverses);
  
  if (issues.critical.length > 0) {
    console.error(`[UniverseManager] Critical issues in universe ${universe.id}:`, issues.critical);
    
    // Log to localStorage for later analysis
    const log = JSON.parse(localStorage.getItem(STORAGE_KEYS.UNIVERSE_VALIDATION_LOG) || '[]');
    log.push({
      timestamp: new Date().toISOString(),
      universeId: universe.id,
      issues: issues.critical
    });
    localStorage.setItem(STORAGE_KEYS.UNIVERSE_VALIDATION_LOG, JSON.stringify(log.slice(-50))); // Keep last 50
  }

  return {
    valid: issues.critical.length === 0,
    issues
  };
}

/**
 * Get validation log from localStorage
 */
export function getValidationLog() {
  return JSON.parse(localStorage.getItem(STORAGE_KEYS.UNIVERSE_VALIDATION_LOG) || '[]');
}

/**
 * Clear validation log
 */
export function clearValidationLog() {
  localStorage.removeItem(STORAGE_KEYS.UNIVERSE_VALIDATION_LOG);
}

const UniverseManager = {
  validateUniverse,
  validateAllUniverses,
  getValidationSummary,
  validateUniverseOnEnter,
  getValidationLog,
  clearValidationLog
};

export default UniverseManager;